import type { ExperienceBlockDraft, ExperienceVariable } from '@matter_extractor/shared';
import { type ReactNode, useId } from 'react';
import { READER_TEST_IDS, type ReaderMode } from '../contracts';
import { useRadioGroupKeys } from './radio-group';
import { VariableText } from './variable-text';

export interface SelectionBlockProps {
  readonly block: Extract<ExperienceBlockDraft, { type: 'selection' }>;
  readonly mode: ReaderMode;
  /** The LABEL of the option that is lit, or the empty string. */
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly resolveVariable: ((variable: ExperienceVariable) => string | null) | undefined;
  /** A preview NAMES its variables rather than resolving them (D4, D5) - see `reader-text.ts`. */
  readonly variableNames: ((variable: ExperienceVariable) => string) | undefined;
}

/**
 * The Sélection block (retour-experience.md §4.1; issue #176): a question and 2
 * to 5 pastilles side by side, ONE lit at a time.
 *
 * ## The value is the LABEL of the option
 *
 * Not an index: the token `{{champ:<id>}}` renders what the person chose, and
 * Sessions reads it the way it reads an Input. It follows that a lit pastille
 * is « the option whose label is the value », which is also what makes the
 * RESUME work with nothing more (§5.3 of the refonte): a page reopened hands
 * back the value it wrote and the same pastille lights. A value that matches no
 * option - an option renamed since - lights none, and an EMPTY value never
 * lights the empty option of a draft.
 *
 * ## A second click turns it off, unless the block is required
 *
 * « Un second clic sur la pastille allumée l'éteint, sauf sur une Sélection
 * obligatoire » - a required one has no empty state to go back to, and a
 * press that silently greyed the button of the page would be a trap.
 *
 * ## A radiogroup, not a row of buttons
 *
 * Each pastille is a `<button role="radio">` so that a click, Espace and Entrée
 * are the button's own, and the group is named by its question
 * (`aria-labelledby`). The arrows are `useRadioGroupKeys`'s.
 *
 * Nothing here writes at the click but the page's own value: « la valeur part
 * avec les autres valeurs de la page, jamais à chaque clic » is the caller's
 * business - the session posts it at the Bouton.
 */
export function SelectionBlock({
  block,
  mode,
  value,
  onChange,
  resolveVariable,
  variableNames,
}: SelectionBlockProps): ReactNode {
  const labelId = useId();
  const interactive = mode === 'interactive';
  const lit = value === '' ? -1 : block.options.indexOf(value);
  const keys = useRadioGroupKeys(block.options.length, lit === -1 ? null : lit);

  const pick = (option: string): void => {
    if (!interactive) return;
    if (option === value) {
      if (!block.required) onChange('');
      return;
    }
    onChange(option);
  };

  return (
    <div className="lecteur-champ">
      <p className="lecteur-etiquette" id={labelId}>
        <VariableText
          text={block.question}
          resolveVariable={resolveVariable}
          variableNames={variableNames}
        />
      </p>
      <div
        className="lecteur-selection"
        data-testid={READER_TEST_IDS.selection}
        role="radiogroup"
        aria-labelledby={labelId}
        aria-required={block.required}
        onKeyDown={keys.onKeyDown}
      >
        {block.options.map((option, index) => (
          <button
            // A DRAFT may hold two options of one label (the refusal `selection-options`
            // says so): the index keeps their keys apart.
            key={`${index}-${option}`}
            type="button"
            // biome-ignore lint/a11y/useSemanticElements: the APG radio group - a `<button role="radio">` - is what lets a pastille be drawn as one and be turned off by a second click, which a native radio cannot
            role="radio"
            className="lecteur-option"
            data-testid={READER_TEST_IDS.selectionOption}
            aria-checked={index === lit}
            tabIndex={keys.tabIndexOf(index)}
            onFocus={keys.onFocusOf(index)}
            onClick={() => pick(option)}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}
